import { Fragment, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { ChevronDown, ExternalLink, Info } from "lucide-react";
import { COUNTRY_CONFIG } from "../config/countries";
import type {
  ProviderCondition,
  ResolvedProvider,
} from "../config/energyProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  computeDisplayedMonthlyEstimate,
  type DisplayedMonthlyEstimate,
} from "@/utils/priceCalculations";
import {
  activeConditions,
  availableFilters,
  matchesFilter,
  providerDisplayName,
  type ProviderFilterId,
} from "@/utils/providerConditions";
import { formatCoefficient, formatEuro } from "@/utils/format";
import type { ExpectedPrices } from "@/services/expectedPriceApi";
import { STRINGS } from "@/i18n";
import { ProviderChips } from "./ProviderChips";
import { PER_KWH_DECIMALS, ProviderDetails } from "./ProviderDetails";
import { ProviderFilters } from "./ProviderFilters";

const EXAMPLE_USAGE_KWH = 200;

type SortBy = "name" | "fee" | "coefficient" | "charge" | "monthlyExample";

interface ProviderComparison {
  provider: ResolvedProvider;
  /** Conditions in force today, in the order of the providers file. */
  conditions: ProviderCondition[];
  /** The estimate as displayed: the shown total is the sum of the shown amounts. */
  estimate: DisplayedMonthlyEstimate | null;
}

// Column widths of the desktop table, as classes on <col>. From 1280px, where the table is
// always 1214px wide, the widths are fixed to the layout the browser chose while a row was
// expanded, so the headers no longer move when a row opens or closes. Below that the browser
// sizes the columns to their content; the expanded panel does not take part in that (see the
// details row), so the headers stay put there too.
const DESKTOP_COLUMNS = {
  // Naam, Vastrecht, Toeslag, Indicatie maandlast, Link
  nl: ["xl:w-[25.4%]", "xl:w-[15.8%]", "xl:w-[16.2%]", "xl:w-[23.2%]", "xl:w-[19.4%]"],
  // Naam, Vastrecht, Toeslag, Factor, Indicatie maandlast, Link
  be: [
    // Vastrecht is 1.6 points wider than in the measured layout so its 15px header fits.
    "xl:w-[30.8%]",
    "xl:w-[14.1%]",
    "xl:w-[12.8%]",
    "xl:w-[8.8%]",
    "xl:w-[18.3%]",
    "xl:w-[15.2%]",
  ],
} as const;

// Fixed width of the desktop "Bezoeken" buttons; the "Link" header uses it to centre itself.
const VISIT_BUTTON_WIDTH = "w-32";

const SELECTED_ROW_CLASS =
  "bg-[color-mix(in_srgb,var(--primary)_12%,transparent)] hover:bg-[color-mix(in_srgb,var(--primary)_12%,transparent)]";

interface ProvidersTableProps {
  includeTaxes: boolean;
  selectedProvider?: string;
  providers: ResolvedProvider[];
  averageSpotPrice?: number;
  /** Expected 12-month price; used instead of `averageSpotPrice` when the country has a source. */
  expectedPrices?: ExpectedPrices;
  priceDate: Date;
  regionId?: string | null;
}

export function ProvidersTable({
  includeTaxes,
  selectedProvider,
  providers,
  averageSpotPrice,
  expectedPrices,
  priceDate,
  regionId,
}: ProvidersTableProps) {
  const [sortBy, setSortBy] = useState<SortBy>("monthlyExample");
  const [activeFilters, setActiveFilters] = useState<
    ReadonlySet<ProviderFilterId>
  >(() => new Set());
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const t = STRINGS.providers;
  const showSpotCoefficient = COUNTRY_CONFIG.country === "be";
  const columnCount = showSpotCoefficient ? 6 : 5;
  const desktopColumns = DESKTOP_COLUMNS[showSpotCoefficient ? "be" : "nl"];
  const includeAffiliateLinks =
    import.meta.env.VITE_INCLUDE_AFFILIATE_LINKS === "true";
  const getProviderLink = (link: ResolvedProvider) =>
    includeAffiliateLinks ? (link.affiliateLink ?? link.url) : link.url;
  // an affiliate link says so to search engines
  const getProviderRel = (link: ResolvedProvider) =>
    getProviderLink(link) === link.url
      ? "noopener noreferrer"
      : "sponsored noopener noreferrer";

  const applyVat = (value: number) =>
    includeTaxes ? value * (1 + COUNTRY_CONFIG.vatRate) : value;
  const euroPerKwh = (value: number) => formatEuro(value, PER_KWH_DECIMALS);

  const expectedPriceSource = COUNTRY_CONFIG.expectedPriceSource;
  const useExpectedPrice = Boolean(expectedPriceSource && expectedPrices);
  const spotPrice =
    useExpectedPrice && expectedPrices
      ? expectedPrices.electricity
      : averageSpotPrice;
  const energyTaxNote = includeTaxes
    ? t.inclTaxClause(COUNTRY_CONFIG.energyTaxLabel)
    : "";
  const estimateExplanation =
    useExpectedPrice && expectedPriceSource ? (
      <>
        {t.estimateBasisExpected(EXAMPLE_USAGE_KWH, energyTaxNote)} ({t.source}:{" "}
        <a
          href={expectedPriceSource.url}
          target="_blank"
          rel="noopener noreferrer"
          title={expectedPriceSource.fullName}
          className="underline underline-offset-2 hover:no-underline"
        >
          {expectedPriceSource.name}
        </a>
        )
      </>
    ) : (
      <>{t.estimateBasisDay(EXAMPLE_USAGE_KWH, energyTaxNote)}</>
    );

  // The expected price looks ahead, so it uses today's tax rate rather than the chart date.
  const energyTaxRate = COUNTRY_CONFIG.getEnergyTaxRate(
    useExpectedPrice ? new Date() : priceDate,
    regionId ?? undefined
  );

  const comparisons = useMemo<ProviderComparison[]>(() => {
    const today = new Date();
    return providers.map((provider) => ({
      provider,
      conditions: activeConditions(provider.conditions, today),
      estimate:
        spotPrice === undefined
          ? null
          : computeDisplayedMonthlyEstimate({
              spotPrice,
              perKwhMarkup: provider.perKwhChargeExcludingVat,
              spotCoefficient: provider.spotCoefficient,
              energyTaxRate,
              vatRate: COUNTRY_CONFIG.vatRate,
              includeTaxes,
              usageKwh: EXAMPLE_USAGE_KWH,
              monthlyFee: provider.monthlyFee,
              perKwhDecimals: PER_KWH_DECIMALS,
            }),
    }));
  }, [energyTaxRate, includeTaxes, providers, spotPrice]);

  const filters = useMemo(
    () => availableFilters(comparisons.map((entry) => entry.conditions)),
    [comparisons]
  );
  // A filter that cannot change this list (e.g. after a region switch) is ignored, not applied.
  const effectiveFilters = useMemo(
    () => filters.filter((filter) => activeFilters.has(filter)),
    [activeFilters, filters]
  );

  const visibleComparisons = useMemo(() => {
    const byName = (a: ProviderComparison, b: ProviderComparison) =>
      providerDisplayName(a.provider).localeCompare(
        providerDisplayName(b.provider)
      );
    const compare = (a: ProviderComparison, b: ProviderComparison) => {
      switch (sortBy) {
        case "fee":
          return a.provider.monthlyFee - b.provider.monthlyFee || byName(a, b);
        case "coefficient":
          return (
            (a.provider.spotCoefficient ?? 1) -
              (b.provider.spotCoefficient ?? 1) || byName(a, b)
          );
        case "charge":
          return (
            a.provider.perKwhChargeExcludingVat -
              b.provider.perKwhChargeExcludingVat || byName(a, b)
          );
        case "monthlyExample":
          return (
            (a.estimate?.total ?? Number.POSITIVE_INFINITY) -
              (b.estimate?.total ?? Number.POSITIVE_INFINITY) || byName(a, b)
          );
        case "name":
        default:
          return byName(a, b);
      }
    };
    return comparisons
      .filter((entry) =>
        effectiveFilters.every((filter) =>
          matchesFilter(entry.conditions, filter)
        )
      )
      .sort(compare);
  }, [comparisons, effectiveFilters, sortBy]);

  const toggleExpanded = (id: string) =>
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleFilter = (filter: ProviderFilterId) =>
    setActiveFilters((previous) => {
      const next = new Set(previous);
      if (next.has(filter)) next.delete(filter);
      else next.add(filter);
      return next;
    });
  const clearFilters = () => setActiveFilters(new Set());
  // The whole row toggles the details, except its links and buttons.
  const handleRowClick = (event: MouseEvent<HTMLElement>, id: string) => {
    if (event.target instanceof Element && event.target.closest("a, button")) {
      return;
    }
    toggleExpanded(id);
  };

  const renderDetails = (
    { provider, conditions, estimate }: ProviderComparison,
    framed = true
  ) => (
    <ProviderDetails
      framed={framed}
      provider={provider}
      conditions={conditions}
      estimate={estimate}
      spotPrice={spotPrice}
      includeTaxes={includeTaxes}
      highlighted={provider.id === selectedProvider}
    />
  );

  // A header cell. The sort arrow follows the label inside the cell's right padding, so a
  // label never moves and stays lined up with the values under it. A header with an extra
  // control (the info button) keeps a fixed slot for the arrow between label and control.
  const renderSortableHead = (
    key: SortBy,
    label: string,
    align: "left" | "right",
    extra?: ReactNode
  ) => {
    const isSorted = sortBy === key;
    return (
      <TableHead
        aria-sort={isSorted ? "ascending" : undefined}
        onClick={() => setSortBy(key)}
        className={cn(
          "cursor-pointer select-none whitespace-nowrap hover:bg-muted/60",
          // Right-aligned columns need little room on the left: the previous column ends
          // in its own right padding.
          align === "left" ? "pl-14 pr-3 text-left" : "pl-1 pr-3 text-right",
          isSorted && "text-primary"
        )}
      >
        <span className="relative inline-flex items-center gap-1 align-middle">
          {label}
          {extra ? (
            <>
              <span aria-hidden="true" className="inline-block w-3 text-center text-xs">
                {isSorted ? "↓" : ""}
              </span>
              {extra}
            </>
          ) : (
            isSorted && (
              <span
                aria-hidden="true"
                // 12px keeps the arrow inside the 12px right padding of the cell.
                className="absolute left-full top-1/2 ml-px -translate-y-1/2 text-xs"
              >
                ↓
              </span>
            )
          )}
        </span>
      </TableHead>
    );
  };

  const renderNameAndProduct = (provider: ResolvedProvider) => (
    <>
      <span>{provider.name}</span>
      {provider.product && (
        <span className="text-sm font-normal text-muted-foreground">
          {provider.product}
        </span>
      )}
    </>
  );

  return (
    <Card className="overflow-hidden">
      <div className="flex w-full items-center justify-between border-b border-border bg-muted/50 px-6 py-4">
        <h2 className="text-lg font-bold text-foreground">
          {t.title}
        </h2>
        <div className="hidden lg:flex items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/?correctie=1">{STRINGS.correction.open}</Link>
          </Button>
        </div>
      </div>

      <CardContent className="p-0">
        {comparisons.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">
            {t.noneForRegion}
          </div>
        ) : (
          <>
            <ProviderFilters
              available={filters}
              active={activeFilters}
              onToggle={toggleFilter}
              onClear={clearFilters}
              shownCount={visibleComparisons.length}
              totalCount={comparisons.length}
            />

            {visibleComparisons.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                <p>{t.noneMatchFilters}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={clearFilters}
                >
                  {t.clearFilters}
                </Button>
              </div>
            ) : (
              <>
                <div
                  className="md:hidden"
                  data-testid="mobile-provider-comparison"
                >
                  <div className="border-b border-border px-4 py-4">
                    <label
                      htmlFor="provider-sort"
                      className="mb-2 block text-sm font-semibold text-foreground"
                    >
                      {t.sortBy}
                    </label>
                    <Select
                      value={sortBy}
                      onValueChange={(value) => setSortBy(value as SortBy)}
                    >
                      <SelectTrigger
                        id="provider-sort"
                        className="h-11 text-base"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthlyExample">
                          {t.monthlyEstimate}
                        </SelectItem>
                        <SelectItem value="name">{t.name}</SelectItem>
                        <SelectItem value="fee">{t.monthlyFeeLong}</SelectItem>
                        <SelectItem value="charge">{t.markupLong}</SelectItem>
                        {showSpotCoefficient && (
                          <SelectItem value="coefficient">{t.factor}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                      {estimateExplanation}.
                    </p>
                  </div>

                  <ul className="space-y-3 bg-muted/20 p-4">
                    {visibleComparisons.map((entry) => {
                      const { provider, conditions, estimate } = entry;
                      const isSelected = provider.id === selectedProvider;
                      const isExpanded = expandedIds.has(provider.id);
                      const panelId = `mobile-provider-details-${provider.id}`;
                      return (
                        <li key={provider.id}>
                          <article
                            className={cn(
                              "rounded-lg border border-border bg-card p-4 shadow-sm",
                              isSelected &&
                                "border-primary bg-[color-mix(in_srgb,var(--primary)_8%,var(--card))]"
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <h3 className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5 font-semibold text-foreground">
                                {renderNameAndProduct(provider)}
                              </h3>
                              {isSelected && (
                                <span className="shrink-0 rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                                  {t.selected}
                                </span>
                              )}
                            </div>
                            <ProviderChips conditions={conditions} className="mt-2" limit={3} />

                            <div className="mt-4">
                              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                {t.monthlyEstimate}
                              </p>
                              {estimate ? (
                                <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                                  {formatEuro(estimate.total, 2)}
                                </p>
                              ) : (
                                <p
                                  className="mt-1 text-xl font-semibold text-muted-foreground"
                                  aria-label={t.noEstimate}
                                >
                                  —
                                </p>
                              )}
                            </div>

                            <dl className="mt-4 divide-y divide-border border-y border-border text-sm">
                              <div className="flex items-center justify-between gap-4 py-2.5">
                                <dt className="text-muted-foreground">
                                  {t.monthlyFeeLong}
                                </dt>
                                <dd className="font-medium tabular-nums text-foreground">
                                  {formatEuro(provider.monthlyFee, 2)}
                                </dd>
                              </div>
                              <div className="flex items-center justify-between gap-4 py-2.5">
                                <dt className="text-muted-foreground">
                                  {t.markupLong}
                                </dt>
                                <dd className="font-medium tabular-nums text-foreground">
                                  {euroPerKwh(applyVat(provider.perKwhChargeExcludingVat))}
                                  <span className="text-xs font-normal text-muted-foreground">
                                    {" "}
                                    / kWh
                                  </span>
                                </dd>
                              </div>
                              {showSpotCoefficient && (
                                <div className="flex items-center justify-between gap-4 py-2.5">
                                  <dt className="text-muted-foreground">{t.factor}</dt>
                                  <dd className="font-medium tabular-nums text-foreground">
                                    ×{formatCoefficient(provider.spotCoefficient ?? 1)}
                                  </dd>
                                </div>
                              )}
                            </dl>

                            {/* The "Details" row opens the calculation, feed-in and conditions;
                                on phones they follow without a panel of their own. */}
                            <button
                              type="button"
                              aria-expanded={isExpanded}
                              aria-controls={panelId}
                              aria-label={
                                isExpanded
                                  ? t.collapseDetails(providerDisplayName(provider))
                                  : t.expandDetails(providerDisplayName(provider))
                              }
                              onClick={() => toggleExpanded(provider.id)}
                              className="flex min-h-11 w-full items-center justify-between gap-3 text-left text-[15px] font-medium text-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {t.details}
                              <ChevronDown
                                aria-hidden="true"
                                className={cn(
                                  "h-4 w-4 shrink-0 transition-transform",
                                  isExpanded && "rotate-180"
                                )}
                              />
                            </button>

                            {isExpanded && (
                              <div id={panelId} className="pb-2 pt-1">
                                {renderDetails(entry, false)}
                              </div>
                            )}

                            <Button
                              variant="secondary"
                              asChild
                              className="mt-4 w-full bg-primary/10 text-primary hover:bg-primary/20"
                            >
                              <a
                                href={getProviderLink(provider)}
                                target="_blank"
                                rel={getProviderRel(provider)}
                              >
                                <ExternalLink className="h-4 w-4" />
                                {t.visit}
                              </a>
                            </Button>
                          </article>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div
                  className="hidden md:block"
                  data-testid="desktop-provider-comparison"
                >
                  <Table className="xl:table-fixed">
                    <colgroup>
                      {desktopColumns.map((width, index) => (
                        <col key={index} className={width} />
                      ))}
                    </colgroup>
                    <TableHeader>
                      <TableRow className="bg-muted/30 text-[15px] hover:bg-muted/30">
                        {renderSortableHead("name", t.name, "left")}
                        {renderSortableHead("fee", t.monthlyFeeShort, "right")}
                        {renderSortableHead("charge", t.markupShort, "right")}
                        {showSpotCoefficient &&
                          renderSortableHead("coefficient", t.factor, "right")}
                        {renderSortableHead(
                          "monthlyExample",
                          t.monthlyEstimate,
                          "right",
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                onClick={(event) => event.stopPropagation()}
                                className="inline-flex items-center rounded text-muted-foreground hover:text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                                aria-label={t.moreInfoEstimate}
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 whitespace-normal text-xs font-normal">
                              {estimateExplanation}
                            </PopoverContent>
                          </Popover>
                        )}
                        {/* Same width and right padding as the buttons, so the label sits centred
                            above them. */}
                        <TableHead className="pl-1 pr-6 text-right">
                          <span className={cn("inline-block text-center", VISIT_BUTTON_WIDTH)}>
                            {t.link}
                          </span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleComparisons.map((entry) => {
                        const { provider, conditions, estimate } = entry;
                        const isSelected = provider.id === selectedProvider;
                        const isExpanded = expandedIds.has(provider.id);
                        const panelId = `desktop-provider-details-${provider.id}`;
                        // Every cell starts with a 32px line, as high as the button, so the
                        // name, the amounts and the button share one centre line.
                        const valueCellClass = "py-4 pl-1 pr-3 text-right align-top text-base";
                        const valueLineClass = "h-8 whitespace-nowrap leading-8 tabular-nums";
                        return (
                          <Fragment key={provider.id}>
                            <TableRow
                              data-testid={`provider-row-${provider.id}`}
                              onClick={(event) => handleRowClick(event, provider.id)}
                              className={cn(
                                "cursor-pointer",
                                isExpanded && "border-b-0",
                                isSelected && SELECTED_ROW_CLASS
                              )}
                            >
                              <TableCell className="py-4 pl-4 pr-3 align-top text-base">
                                <div className="flex items-start gap-2">
                                  <button
                                    type="button"
                                    aria-expanded={isExpanded}
                                    aria-controls={panelId}
                                    aria-label={
                                      isExpanded
                                        ? t.collapseDetails(providerDisplayName(provider))
                                        : t.expandDetails(providerDisplayName(provider))
                                    }
                                    onClick={() => toggleExpanded(provider.id)}
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                                  >
                                    <ChevronDown
                                      aria-hidden="true"
                                      className={cn(
                                        "h-4 w-4 transition-transform",
                                        isExpanded && "rotate-180"
                                      )}
                                    />
                                  </button>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-baseline gap-x-2 text-[15px] leading-8">
                                      <a
                                        href={getProviderLink(provider)}
                                        target="_blank"
                                        rel={getProviderRel(provider)}
                                        className="inline-flex items-center gap-1 font-medium text-primary underline decoration-2 underline-offset-2 hover:no-underline"
                                      >
                                        {provider.name}
                                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                                      </a>
                                      {provider.product && (
                                        <span className="text-muted-foreground">
                                          {provider.product}
                                        </span>
                                      )}
                                    </div>
                                    <ProviderChips
                                      conditions={conditions}
                                      className="mt-1"
                                    />
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className={valueCellClass}>
                                <div className={valueLineClass}>
                                  {formatEuro(provider.monthlyFee, 2)}
                                </div>
                              </TableCell>
                              <TableCell className={valueCellClass}>
                                <div className={valueLineClass}>
                                  {euroPerKwh(applyVat(provider.perKwhChargeExcludingVat))}{" "}
                                  <span className="text-xs text-muted-foreground">/ kWh</span>
                                </div>
                              </TableCell>
                              {showSpotCoefficient && (
                                <TableCell className={valueCellClass}>
                                  <div className={valueLineClass}>
                                    ×{formatCoefficient(provider.spotCoefficient ?? 1)}
                                  </div>
                                </TableCell>
                              )}
                              <TableCell className={valueCellClass}>
                                <div className={cn(valueLineClass, "font-semibold")}>
                                  {estimate ? (
                                    formatEuro(estimate.total, 2)
                                  ) : (
                                    <span
                                      className="font-normal text-muted-foreground"
                                      aria-label={t.noEstimate}
                                    >
                                      —
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="py-4 pl-1 pr-6 text-right align-top">
                                <div className="flex h-8 items-center justify-end">
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    asChild
                                    className={cn(
                                      "bg-primary/10 text-primary hover:bg-primary/20",
                                      VISIT_BUTTON_WIDTH
                                    )}
                                  >
                                    <a
                                      href={getProviderLink(provider)}
                                      target="_blank"
                                      rel={getProviderRel(provider)}
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                      <span className="text-sm font-semibold">{t.visit}</span>
                                    </a>
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow
                                id={panelId}
                                className={cn(
                                  "border-t-0 hover:bg-transparent",
                                  isSelected && SELECTED_ROW_CLASS
                                )}
                              >
                                <TableCell
                                  colSpan={columnCount}
                                  className="pb-5 pl-14 pr-6 pt-0"
                                >
                                  {/* Zero intrinsic width: the panel fills the row but never
                                      widens a column, so opening it moves no header. */}
                                  <div className="w-0 min-w-full">{renderDetails(entry)}</div>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>

      {COUNTRY_CONFIG.regions && (
        <p className="border-t border-border px-6 py-3 text-xs text-muted-foreground">
          {t.regionNote}
        </p>
      )}

      <div className="border-t border-border px-4 py-4 lg:hidden">
        <Button variant="outline" className="w-full" asChild>
          <Link to="/?correctie=1">{STRINGS.correction.open}</Link>
        </Button>
      </div>
    </Card>
  );
}
